// src/features/task/detail/sections.tsx
// Which parts of a task are actually on the page.
//
// A task can carry checklists, subtasks, time, dependencies and attachments, but
// most tasks carry none of them. Rendering a headed, bordered card that says "no
// checklists yet" for each one turns an empty task into a page of announcements
// about absence — six of them, on the task this was written for. So a part that
// has nothing in it is not drawn; it is offered, the way the create page offers
// the same parts, and it appears the moment there is something to show or the
// moment someone asks for it.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/** The parts a task page can show, in the order they are offered. */
export const TASK_SECTIONS = [
  { name: 'checklists', labelKey: 'task.checklists' },
  { name: 'subtasks', labelKey: 'task.subtasks.title' },
  { name: 'time', labelKey: 'task.timeEntries' },
  { name: 'dependencies', labelKey: 'task.dependencies' },
  { name: 'attachments', labelKey: 'task.attachments' },
] as const;

export type TaskSectionName = (typeof TASK_SECTIONS)[number]['name'];

interface SectionState {
  /** Sections the user has asked to see even though they are empty. */
  revealed: ReadonlySet<string>;
  /** Sections that reported having something in them. */
  filled: ReadonlySet<string>;
  reveal: (name: string) => void;
  report: (name: string, hasContent: boolean) => void;
}

const SectionCtx = createContext<SectionState | null>(null);

export function TaskSectionsProvider({ children }: { children: ReactNode }) {
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(() => new Set());
  const [filled, setFilled] = useState<ReadonlySet<string>>(() => new Set());

  const reveal = useCallback((name: string) => {
    setRevealed((prev) => (prev.has(name) ? prev : new Set(prev).add(name)));
  }, []);

  const report = useCallback((name: string, hasContent: boolean) => {
    setFilled((prev) => {
      if (prev.has(name) === hasContent) return prev;
      const next = new Set(prev);
      if (hasContent) next.add(name);
      else next.delete(name);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ revealed, filled, reveal, report }),
    [revealed, filled, reveal, report],
  );
  return <SectionCtx.Provider value={value}>{children}</SectionCtx.Provider>;
}

/**
 * Called by a section with whether it currently has anything to show. Returns
 * whether it should draw itself.
 *
 * Outside a provider it always draws, so these components stay usable on pages
 * that do not manage sections — the entity panel, for one.
 */
export function useTaskSection(name: TaskSectionName, hasContent: boolean): boolean {
  const ctx = useContext(SectionCtx);
  const report = ctx?.report;

  useEffect(() => {
    report?.(name, hasContent);
  }, [report, name, hasContent]);

  if (!ctx) return true;
  return hasContent || ctx.revealed.has(name);
}

/** The page's view of what is missing, for the row that offers those parts. */
export function useMissingSections() {
  const ctx = useContext(SectionCtx);
  return {
    missing: TASK_SECTIONS.filter(
      (s) => !(ctx?.filled.has(s.name) ?? false) && !(ctx?.revealed.has(s.name) ?? false),
    ),
    reveal: ctx?.reveal ?? (() => {}),
  };
}
